import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, switchMap, throwError, tap, Observable, shareReplay } from 'rxjs';
import { LoaderService } from './loader.service';
import { AlertService } from './alert.service';
import { AuthService } from './auth.service';

let refreshRequest$: Observable<unknown> | null = null;
let logoutInProgress = false;

export const httpInterceptor: HttpInterceptorFn = (req, next) => {
    const loaderService = inject(LoaderService);
    const alertService = inject(AlertService);
    const authService = inject(AuthService);
    const router = inject(Router);

    const shouldSkipLoader = req.headers.has('X-Skip-Loader');
    const isRefreshRequest = req.url.includes('account/token/refresh');
    const isAuthRequest = req.url.includes('account/Login') || req.url.includes('account/otp/verify') || req.url.includes('account/logout');
    const isPasswordRequest = req.url.includes('password/verifytoken') || req.url.includes('password/reset') || req.url.includes('password/forgot');

    if (!shouldSkipLoader) {
        loaderService.show();
    }

    const authReq = req.clone({
        withCredentials: true
    });

    return next(authReq).pipe(
        tap({
            next: (event) => {
                if (event instanceof HttpResponse) {
                    const body = event.body as any;
                    const isVerifyToken = req.url.includes('password/verifytoken');
                    const isPasswordChangeRequest = req.url.includes('password/change');
                    const isPasswordResetRequest = req.url.includes('password/reset');
                    if (body && body.status === false && !isVerifyToken && !isPasswordChangeRequest && !isPasswordResetRequest) {
                        const errorMessage = body.message || 'An error occurred';
                        alertService.error(errorMessage, 'Error');
                    }
                }
            }
        }),
        catchError((error: any) => {
            console.error('HTTP Interceptor caught error:', error);

            if (req.url.includes('password/verifytoken')) {
                return throwError(() => error);
            }

            if (error instanceof HttpErrorResponse && error.status === 401 && !isRefreshRequest && !isAuthRequest && !isPasswordRequest) {
                if (!refreshRequest$) {
                    refreshRequest$ = authService.refreshToken().pipe(
                        shareReplay({ bufferSize: 1, refCount: false }),
                        finalize(() => {
                            refreshRequest$ = null;
                        })
                    );
                }

                return refreshRequest$.pipe(
                    switchMap(() => next(authReq.clone({ withCredentials: true }))),
                    catchError((refreshError: any) => {
                        if (!logoutInProgress) {
                            logoutInProgress = true;
                            authService.clearAuthData();
                            Promise.resolve(router.navigate(['/login'])).finally(() => {
                                logoutInProgress = false;
                            });
                        }
                        return throwError(() => refreshError);
                    })
                );
            }

            let errorMessage = 'An unexpected error occurred';

            if (error instanceof HttpErrorResponse) {
                if (error.error instanceof ErrorEvent) {
                    errorMessage = error.error.message;
                } else {
                    const serverMessage = (typeof error.error === 'string' && error.error.trim())
                        ? error.error
                        : (error.error?.message || error.error?.Message || error.error?.detail || error.error?.error);

                    switch (error.status) {
                        case 0:
                            errorMessage = 'Unable to connect to the server. Please check your internet connection.';
                            break;
                        case 400: {
                            const validationErrors = error.error?.errors;
                            if (validationErrors && typeof validationErrors === 'object') {
                                const firstKey = Object.keys(validationErrors)[0];
                                errorMessage = validationErrors[firstKey]?.[0] || serverMessage || 'Bad request. Please check your input.';
                            } else {
                                errorMessage = serverMessage || 'Bad request. Please check your input.';
                            }
                            break;
                        }
                        case 401:
                            errorMessage = serverMessage || 'Session expired. Please login again.';
                            if (!isAuthRequest && !isPasswordRequest) {
                                authService.clearAuthData();
                                router.navigate(['/login']);
                            }
                            break;
                        case 403:
                            errorMessage = serverMessage || 'You do not have permission to perform this action.';
                            break;
                        case 404:
                            errorMessage = serverMessage || 'The requested resource was not found.';
                            break;
                        case 422:
                            errorMessage = serverMessage || 'Validation error. Please check your input.';
                            break;
                        case 500: {
                            const default500 = 'There seems to be an issue. Please try again later.';
                            errorMessage = (serverMessage && !serverMessage.toLowerCase().includes('internal server error')) ? serverMessage : default500;
                            break;
                        }
                        case 502:
                            errorMessage = serverMessage || 'Bad gateway. The server is temporarily unavailable.';
                            break;
                        case 503:
                            errorMessage = serverMessage || 'Service unavailable. Please try again later.';
                            break;
                        case 504:
                            errorMessage = serverMessage || 'Gateway timeout. The server is taking longer than expected to process your request. Please try again or contact support if the issue persists.';
                            break;
                        default:
                            errorMessage = serverMessage || `Error: ${error.status} - ${error.statusText}`;
                    }
                }
            } else {
                errorMessage = error?.message || 'An unexpected error occurred';
            }

            const isPasswordResetRequest = req.url.includes('password/reset');
            const isPasswordChangeRequest = req.url.includes('password/change');
            const isPasswordResetInlineError = isPasswordResetRequest
              && error instanceof HttpErrorResponse;
            const isPasswordChangeInlineError = isPasswordChangeRequest
              && error instanceof HttpErrorResponse;

            if (!isPasswordResetInlineError && !isPasswordChangeInlineError) {
              alertService.error(errorMessage, 'Error');

            }

            return throwError(() => error);
        }),
        finalize(() => {
            if (!shouldSkipLoader) {
                loaderService.hide();
            }
        })
    );
};
